import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  UserCog,
  Layers,
  Folders,
  Users,
  FileText,
} from "lucide-react";
import ProjectsView from '@/pages/configuracion/ProjectsView';
import CatalogsPageSimple from '@/features/configuracion/catalogos/CatalogsPageSimple';
import SystemSettingsView from '@/pages/configuracion/SystemSettingsView';
import { config } from '@/config/env';
import { filterOutMasterUsers } from '@/shared/lib/userFilters';
import { RolesTableView } from '@/pages/configuracion/roles/rolesTableView';
import { useAuth } from '@/shared/hooks/useAuth';
import UsersList from '@/pages/configuracion/UsersList';
import CompanyInfoView from '@/pages/configuracion/CompanyInfoView';

interface DashboardStats {
  totalModules: number;
  activeModules: number;
  totalProjects: number;
  activeProjects: number;
  totalUsers: number;
  activeUsers: number;
}

type TabType =
  | "modules"
  | "projects"
  | "roles"
  | "catalogs"
  | "menu"
  | "settings"
  | "usuarios"
  | "company";

interface Tab {
  id: TabType;
  name: string;
  icon: React.ElementType;
}

/**
 * AdminPanelWrapper - Versión del panel de administración que funciona dentro del Dashboard principal
 * sin perder el sidebar. Incluye tabs para navegar entre las diferentes secciones.
 */
const AdminPanelWrapper: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // With path="/admin/*" the wildcard is stored under '*', not 'tab'.
  // Derive the active tab directly from the pathname instead.
  const tab = (location.pathname.split("/admin/")[1]?.split("/")[0] || undefined) as TabType | undefined;
  const { user } = useAuth();
  const [, setStats] = useState<DashboardStats>({
    totalModules: 0,
    activeModules: 0,
    totalProjects: 0,
    activeProjects: 0,
    totalUsers: 0,
    activeUsers: 0,
  });
  const [loading, setLoading] = useState(true);

  const tabs = useMemo<Tab[]>(() => {
    const perms: string[] = user?.rbac?.effectivePermissions ?? [];
    const has = (p: string) => perms.some((x) => x.includes(p));

    const items: Tab[] = [];

    //{ id: "modules", name: "Módulos", icon: Boxes },
    //{ id: "menu", name: "Menú", icon: Menu },
    //items.push({ id: "settings", name: "Configuracion", icon: Folders });

    if (has("generales.catalogos.proyectos.read")) {
      items.push({ id: "projects", name: "Proyectos", icon: Folders });
    }

    if (has("generales.catalogos.roles.read")) {
      items.push({ id: "roles", name: "Roles y permisos", icon: UserCog });
    }

    if (has("generales.usuarios.base.read")) {
      items.push({ id: "usuarios", name: "Usuarios", icon: Users });
    }

    if (
      has("generales.catalogos.areas.read") ||
      has("generales.catalogos.puestos.read") ||
      has("generales.catalogos.almacenes") ||
      has("almacen.gestionar.herramientas.ver") ||
      has("proveedores.administracion.proveedores.consultar")
    ) {
      items.push({ id: "catalogs", name: "Catálogos", icon: Layers });
    }

    if (user?.rbac?.isMaster) {
      items.push({ id: "company", name: "RFC y razón social", icon: FileText });
    }

    return items;
  }, [user?.rbac?.effectivePermissions]);

  // Derive activeTab from URL param; fall back to first available tab
  const activeTab = (tabs.some(t => t.id === (tab as TabType)) ? tab : tabs[0]?.id) as TabType | undefined;

  // Redirect to default tab if no valid tab in URL
  useEffect(() => {
    if (!loading && tabs.length > 0 && !tabs.some(t => t.id === tab)) {
      navigate(`/admin/${tabs[0].id}`, { replace: true });
    }
  }, [loading, tabs, tab, navigate]);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // Hacer las peticiones en paralelo
      const [modulesResponse, projectsResponse, usersResponse] =
        await Promise.all([
          fetch(`${config.apiBaseUrl}/modules`, { headers }),
          fetch(`${config.apiBaseUrl}/projects`, { headers }),
          fetch(`${config.apiBaseUrl}/users`, { headers }),
        ]);

      const modules = modulesResponse.ok ? await modulesResponse.json() : [];
      const projects = projectsResponse.ok ? await projectsResponse.json() : [];
      const usersRaw = usersResponse.ok ? await usersResponse.json() : [];
      const users = filterOutMasterUsers(usersRaw.data || usersRaw);

      setStats({
        totalModules: modules.length,
        activeModules: modules.filter((m: any) => m.isActive).length,
        totalProjects: projects.length,
        activeProjects: projects.filter((p: any) => p.status === "active")
          .length,
        totalUsers: users.length,
        activeUsers: users.filter((u: any) => u.isActive).length,
      });
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "projects":
        return <ProjectsView />;
      case "roles":
        return <RolesTableView />;
      case "catalogs":
        return <CatalogsPageSimple />;
      case "settings":
        return <SystemSettingsView />;
      case "company":
        return <CompanyInfoView />;
      case "usuarios":
        return <UsersList />;
      default:
        return <></>;
    }
  };

  if (loading) {
    // Mismo loader de entrada que las pantallas de Almacén (color de marca y
    // mismo grosor), para no romper el diseño al llegar desde ese módulo.
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-[#253579] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="m-6">
        <TituloDePantalla className="mb-2">Configuración</TituloDePantalla>
        <p className="text-[#1E1E1F] text-sm">
          Gestiona módulos, proyectos y permisos de usuarios desde un lugar
          central.
        </p>
      </div>

      {/* Tabs Navigation */}
      <div className="overflow-hidden">
        <nav className="flex gap-1 px-4 border-b" style={{ borderColor: "#E0E0E0" }} aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => navigate(`/admin/${tab.id}`)}
                className={`group inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${
                  isActive
                    ? "border-[#477FFA] font-semibold"
                    : "border-transparent hover:border-gray-200"
                }`}
                style={{ color: "#1E1E1F" }}
              >
                <Icon
                  className={`h-4 w-4 flex-shrink-0 ${
                    isActive ? "text-[#477FFA]" : "text-gray-400 group-hover:text-gray-600"
                  }`}
                />
                {tab.name}
              </button>
            );
          })}
        </nav>

        {/* Tab Content */}
        <div className="p-6 bg-gray-50">{renderTabContent()}</div>
      </div>
    </div>
  );
};

export default AdminPanelWrapper;
